import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getProducts, deleteProduct } from "../Redux/Reducers/ProductSlice";
import DefaultAvatar from "../otherImages/default.png";
import { getPaginationTotal, getRowsPerPageOptions } from "../helper/tablePagination";
import useTableUrlState from "../hook/useTableUrlState";
import { getSortOrder, sortTableData } from "../helper/tableSort";

const ProductDataTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { products, categories, loading, error, pagination } = useSelector(
    (state) => state.products
  );

  const [
    { page, rowsPerPage, searchText, selectedCategoryName, sortName, sortDirection },
    setTableState,
    getReturnState,
  ] = useTableUrlState({
    page: 0,
    rowsPerPage: 12,
    searchText: "",
    selectedCategoryName: "",
    sortName: "",
    sortDirection: "",
  });
  const [isSearchUpdate, setIsSearchUpdate] = useState(false);

  const selectedCategory = categories.find(
    (category) => category.name === selectedCategoryName
  );

  useEffect(() => {
    dispatch(
      getProducts({
        page: page + 1,
        perPage: rowsPerPage,
        search: searchText,
        category: selectedCategory?.id || "",
      })
    );
  }, [dispatch, page, rowsPerPage, searchText, selectedCategory?.id]);

  useEffect(() => {
    if (!loading) {
      setIsSearchUpdate(false);
    }
  }, [loading]);

  const handleDeletePackage = async (id, productName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to delete product "${productName}"?`,
      icon: "warning",
      confirmButtonColor: "#000",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(deleteProduct(id)).unwrap();
        
        if (response?.success) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: response?.message || "Product has been deleted successfully.",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: response?.message || "Failed to delete product.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error || "Something went wrong while deleting the product.",
        });
      }
    }
  };

  // Get product image
  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      const primaryImage = product.images.find(
        (img) =>
          img.is_primary === true ||
          img.is_primary === 1 ||
          img.is_primary === "1" ||
          img.is_primray === true ||
          img.is_primray === 1 ||
          img.is_primray === "1"
      );
      if (primaryImage) {
        return primaryImage.image_url || primaryImage.url;
      }
      return product.images[0]?.image_url || product.images[0]?.url;
    }
    return DefaultAvatar;
  };

  // Get product price
  const getProductPrice = (product) => {
    const buyPrice = product.buy_price || product.variants?.[0]?.buy_price || 0;
    const rentPrice = product.rent_price || product.variants?.[0]?.rent_price || null;

    return { buy: buyPrice, rent: rentPrice };
  };

  // Get total stock
  const getTotalStock = (product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((total, variant) => {
        return total + (variant.stock_quantity || 0);
      }, 0);
    }
    return 0;
  };

  const sortedProducts = useMemo(
    () =>
      sortTableData(products, sortName, sortDirection, {
        category: (product) => product?.category?.name || "",
        price: (product) => getProductPrice(product).buy || getProductPrice(product).rent || 0,
        stock: getTotalStock,
      }),
    [products, sortName, sortDirection],
  );

  const columns = [
    {
      name: "name",
      label: "Product Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedProducts[tableMeta.rowIndex];
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                style={{
                  height: "35px",
                  width: "35px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                src={getProductImage(rowData)}
                alt={value}
                onError={(e) => {
                  e.target.src = DefaultAvatar;
                }}
              />
              <div>
                <div className="fw-medium">{value}</div>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: "created_at",
      label: "Date",
      options: {
        customBodyRender: (value) => {
          if (!value) return "-";
          const date = new Date(value);
          return date.toLocaleDateString("en-GB", {
            className: "date",
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
    },
    {
      name: "stock",
      label: "Stock",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedProducts[tableMeta.rowIndex];
          const stock = getTotalStock(rowData);
          const isInStock = stock > 0;
          return (
            <span className={`badge ${isInStock ? "bg-success" : "bg-danger"}`}>
              {isInStock ? "In stock" : "Out of stock"}
            </span>
          );
        },
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedProducts[tableMeta.rowIndex];
          const { buy, rent } = getProductPrice(rowData);

          return (
            <div className="d-flex flex-column gap-2">
              {buy > 0 && <div className={`col-packageType rounded-full text-xs font-medium`}>Buy: ${parseFloat(buy).toFixed(2)}</div>}
              {rent > 0 && <div className={`col-packageType rounded-full text-xs font-medium`}>Rent: ${parseFloat(rent).toFixed(2)}</div>}
              {!buy && !rent && "-"}
            </div>
          );
        },
      },
    },
    {
      name: "category",
      label: "Category",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedProducts[tableMeta.rowIndex];
          const categoryName = rowData?.category?.name || 
            (rowData?.category_id === 3 ? "Ties" :
             rowData?.category_id === 5 ? "Pocket Squares" : "Other");

          return (
            <span className={`col-packageType rounded-full font-medium`}>
              {categoryName}
            </span>
          );
        },
      },
    },
    {
      name: "id",
      label: "Manage",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedProducts[tableMeta.rowIndex];
          const productName = rowData?.name || `Product #${rowData?.id || "-"}`;
          return (
            <div className="d-flex justify-content-center">
              <Icon
                onClick={() =>
                  navigate(`/edit-product/${rowData.id}`, {
                    state: getReturnState(),
                  })
                }
                className="editBtn hover: cursor-pointer"
                icon="line-md:edit"
                width="24"
                height="24"
              />
              <Icon
                onClick={() => handleDeletePackage(rowData.id, productName)}
                className="deleteBtn hover: cursor-pointer"
                icon="material-symbols:delete-outline"
                width="24"
                height="24"
              />
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(
      getPaginationTotal(pagination, products.length)
    ),
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setIsSearchUpdate(false);
      setTableState({
        sortName: direction === "none" ? "" : changedColumn,
        sortDirection: direction === "none" ? "" : direction,
      });
    },
    searchText: searchText,
    onSearchChange: (searchValue) => {
      setIsSearchUpdate(true);
      setTableState({ searchText: searchValue || "", page: 0 });
    },
    customToolbar: () => {
      return (
        <div className="status-filter-dropdown2">
          <select
            className="form-select"
            value={selectedCategoryName}
            onChange={(e) => {
              setIsSearchUpdate(false);
              setTableState({ selectedCategoryName: e.target.value, page: 0 });
            }}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      );
    },
    serverSide: true,
    count: pagination?.total_items || products.length,
    page: page,
    onChangePage: (newPage) => {
      setIsSearchUpdate(false);
      setTableState({ page: newPage });
    },
    onChangeRowsPerPage: (numberOfRows) => {
      setIsSearchUpdate(false);
      setTableState({ rowsPerPage: numberOfRows, page: 0 });
    },
  };

  if (loading && !isSearchUpdate) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="text-danger">
            <Icon icon="material-symbols:error-outline" width="48" height="48" />
            <p className="mt-3">Error: {error}</p>
            <button
              className="btn btn-primary mt-3"
              onClick={() =>
                dispatch(
                  getProducts({
                    page: page + 1,
                    perPage: rowsPerPage,
                    search: searchText,
                    category: selectedCategory?.id || "",
                  })
                )
              }
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card basic-data-table productTable">
      <div className="card-body">
        <MUIDataTable
          data={sortedProducts}
          columns={columns}
          options={options}
          className="overflow-hidden packageTable"
        />
      </div>
    </div>
  );
};

export default ProductDataTable;
